import React, { useState, useMemo, useEffect } from 'react';
import { useRouting } from '@/hooks/useRouting';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { Address, RouteLeg } from '@/types/data';
import { EmptyState } from '@/components/common/EmptyState';

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

interface AddressFormProps {
  title: string;
  address: Address;
  onChange: (address: Address) => void;
  suggestions: Address[];
}

const AddressForm: React.FC<AddressFormProps> = ({ title, address, onChange, suggestions }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Filter suggestions based on current input
  const filteredSuggestions = useMemo(() => {
    const suburbQuery = address.suburb.trim().toUpperCase();
    const postcodeQuery = address.postcode.trim();

    // Need at least some input to show suggestions
    if (!suburbQuery && !postcodeQuery) return [];

    return suggestions
      .filter((s) => {
        // Independent filtering - match by suburb OR postcode (whichever has a value)
        const matchesSuburb = suburbQuery && s.suburb.toUpperCase().startsWith(suburbQuery);
        const matchesPostcode = postcodeQuery && s.postcode.startsWith(postcodeQuery);
        return matchesSuburb || matchesPostcode;
      })
      .slice(0, 8);
  }, [address.suburb, address.postcode, suggestions]);

  const handleSelectSuggestion = (suggestion: Address) => {
    // Clear any pending blur timeout to prevent race condition
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Clear any existing timeout first
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    // Delay hiding to allow click on suggestion to register
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      blurTimeoutRef.current = null;
    }, 150);
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs">
          {title === 'Origin' ? 'A' : 'B'}
        </span>
        {title}
      </h4>

      <div className="space-y-3">
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Suburb</label>
          <input
            type="text"
            value={address.suburb}
            onChange={(e) => {
              onChange({ ...address, suburb: e.target.value.toUpperCase() });
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={handleBlur}
            placeholder="e.g. SYDNEY"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />

          {/* Suggestions dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <>
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.postcode}-${suggestion.suburb}-${index}`}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
                  >
                    <span className="font-medium">{suggestion.suburb}</span>
                    <span className="text-gray-500 ml-2">
                      {suggestion.postcode}, {suggestion.state}
                    </span>
                  </button>
                ))}
              </div>
              {/* Spacer to prevent overlap with fields below */}
              <div className="h-48" />
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Postcode</label>
            <input
              type="text"
              value={address.postcode}
              onChange={(e) => {
                onChange({ ...address, postcode: e.target.value });
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={handleBlur}
              placeholder="e.g. 2000"
              maxLength={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
            <select
              value={address.state}
              onChange={(e) => onChange({ ...address, state: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
            >
              <option value="">Select state</option>
              {AUSTRALIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RouteLegCardProps {
  leg: RouteLeg;
  index: number;
  isLast: boolean;
}

const RouteLegCard: React.FC<RouteLegCardProps> = ({ leg, index, isLast }) => {
  const getLegTypeColor = (fromType: string, toType: string) => {
    if (fromType === 'Locality' && toType === 'Hub') return 'bg-emerald-500'; // Pickup
    if (fromType === 'Hub' && toType === 'Locality') return 'bg-blue-500'; // Dropoff
    return 'bg-amber-500'; // Transfer
  };

  const getLegTypeLabel = (fromType: string, toType: string) => {
    if (fromType === 'Locality' && toType === 'Hub') return 'PICKUP';
    if (fromType === 'Hub' && toType === 'Locality') return 'DROPOFF';
    return 'TRANSFER';
  };

  return (
    <div className="relative">
      {/* Connection line */}
      {!isLast && <div className="absolute left-5 top-full w-0.5 h-4 bg-gray-300" />}

      <div className="flex items-start gap-3">
        {/* Step indicator */}
        <div
          className={`w-10 h-10 rounded-full ${getLegTypeColor(leg.from.type, leg.to.type)} text-white flex items-center justify-center text-sm font-bold flex-shrink-0`}
        >
          {index + 1}
        </div>

        {/* Leg details */}
        <div className="flex-grow bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span
              className={`text-xs font-bold px-2 py-1 rounded ${getLegTypeColor(leg.from.type, leg.to.type)} text-white`}
            >
              {getLegTypeLabel(leg.from.type, leg.to.type)}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-primary-700 bg-primary-100 px-2 py-1 rounded">
                {leg.courier.code}
              </span>
              <span className="text-xs text-gray-500">Zone: {leg.courier.sortCode}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">From ({leg.from.type})</div>
              <div className="font-medium text-gray-800">{leg.from.name}</div>
              {leg.from.address.suburb && (
                <div className="text-sm text-gray-600">
                  {leg.from.address.suburb}, {leg.from.address.state} {leg.from.address.postcode}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-gray-500 mb-1">To ({leg.to.type})</div>
              <div className="font-medium text-gray-800">{leg.to.name}</div>
              {leg.to.address.suburb && (
                <div className="text-sm text-gray-600">
                  {leg.to.address.suburb}, {leg.to.address.state} {leg.to.address.postcode}
                </div>
              )}
            </div>
          </div>

          {leg.courier.account && (
            <div className="mt-2 text-xs text-gray-500">Account: {leg.courier.account}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const TestRouteTab: React.FC = () => {
  const { isLoading, error, result, findRoute, clearResult, localities, isRoutingAvailable } =
    useRouting();
  const { currentNetwork } = useNetworkContext();

  const [origin, setOrigin] = useState<Address>({ suburb: '', postcode: '', state: '' });
  const [destination, setDestination] = useState<Address>({ suburb: '', postcode: '', state: '' });

  // Clear route result when switching between networks/configurations
  useEffect(() => {
    clearResult();
  }, [currentNetwork?.id, clearResult]);

  const isFormValid =
    origin.suburb &&
    origin.postcode &&
    origin.state &&
    destination.suburb &&
    destination.postcode &&
    destination.state;

  const handleFindRoute = () => {
    if (!isFormValid) return;
    findRoute(origin, destination);
  };

  const handleClear = () => {
    setOrigin({ suburb: '', postcode: '', state: '' });
    setDestination({ suburb: '', postcode: '', state: '' });
    clearResult();
  };

  const handleSwap = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
    clearResult();
  };

  if (!isRoutingAvailable) {
    return (
      <EmptyState
        message="Routing data not available"
        subMessage="Please upload serviceability, hubs, and connections files to test routes."
      />
    );
  }

  return (
    <div className="py-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-1">Test Route</h3>
        <p className="text-sm text-gray-500">
          Enter origin and destination addresses to find the optimal delivery route and courier
          assignment.
        </p>
      </div>

      {/* Address Forms */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <AddressForm
          title="Origin"
          address={origin}
          onChange={setOrigin}
          suggestions={localities}
        />
        <AddressForm
          title="Destination"
          address={destination}
          onChange={setDestination}
          suggestions={localities}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleFindRoute}
          disabled={!isFormValid || isLoading}
          className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Finding Route...
            </span>
          ) : (
            'Find Route'
          )}
        </button>

        <button
          onClick={handleSwap}
          className="px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
          title="Swap origin and destination"
        >
          <svg
            width="24px"
            height="24px"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.99268 11.0158C4.44039 11.0158 3.99268 10.5681 3.99268 10.0158C3.99268 9.65844 4.18016 9.34484 4.46214 9.16801L7.14976 6.48039C7.54029 6.08987 8.17345 6.08987 8.56398 6.48039C8.9545 6.87092 8.9545 7.50408 8.56398 7.89461L7.44275 9.01583L14.9927 9.01583C15.545 9.01583 15.9927 9.46355 15.9927 10.0158C15.9927 10.5681 15.545 11.0158 14.9927 11.0158L5.04213 11.0158C5.033 11.016 5.02388 11.016 5.01476 11.0158H4.99268Z"
              fill="black"
            />
            <path
              d="M19.0076 12.984C19.5599 12.984 20.0076 13.4318 20.0076 13.984C20.0076 14.3414 19.8201 14.655 19.5381 14.8319L16.8505 17.5195C16.46 17.91 15.8268 17.91 15.4363 17.5195C15.0457 17.129 15.0457 16.4958 15.4363 16.1053L16.5575 14.984H9.00757C8.45528 14.984 8.00757 14.5363 8.00757 13.984C8.00757 13.4318 8.45528 12.984 9.00757 12.984L18.9581 12.984C18.9672 12.9839 18.9764 12.9839 18.9855 12.984H19.0076Z"
              fill="black"
            />
          </svg>
        </button>

        <button
          onClick={handleClear}
          className="px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-red-700 font-medium">Route not found</span>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800">Route Found</h4>
            <span className="text-sm text-gray-500">
              {result.legs.length} leg{result.legs.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Route Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-500 mb-1">Depot Code</div>
                <div className="font-semibold text-primary-700">
                  {result.compatibility.depotCode}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Lyne Sort Code</div>
                <div className="font-semibold text-primary-700">
                  {result.compatibility.lyneSortCode}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Courier</div>
                <div className="font-semibold text-primary-700">
                  {result.legs[result.legs.length - 1]?.courier.code || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Route Legs */}
          <div className="space-y-4">
            {result.legs.map((leg, index) => (
              <RouteLegCard
                key={`${leg.from.id}-${leg.to.id}-${index}`}
                leg={leg}
                index={index}
                isLast={index === result.legs.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Help text */}
      {!result && !error && (
        <div className="text-sm p-4 rounded-lg bg-primary-50 border border-primary-100 text-primary-700">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1 text-primary-600">
            <li>Enter the pickup (origin) and delivery (destination) addresses</li>
            <li>The system will find the optimal route using your uploaded routing data</li>
            <li>Results show which courier handles each leg of the journey</li>
            <li>Start typing a suburb or postcode for autocomplete suggestions</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default TestRouteTab;
